import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useConfiguratorStore,
  LOADING_STAGES,
  LOADING_MIN_MS,
  type LoadingState,
} from '@/state/configuratorStore';

const BOOT_TIPS = [
  'Drag to orbit · scroll to zoom',
  'Click a zone on the garment to recolour it',
  'Mix patterns per area in Customise',
  'Text, logos and numbers live on the back panel',
];

function stageIndex(stage: string): number {
  const i = LOADING_STAGES.findIndex(s => s.id === stage);
  return i >= 0 ? i : 0;
}

function ProgressRing({ progress, size = 88 }: { progress: number; size?: number }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, progress)) / 100) * c;
  return (
    <div className="boot-ring" style={{ width: size, height: size }} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="boot-ring__track" cx={size / 2} cy={size / 2} r={r} />
        <circle
          className="boot-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="boot-ring__label">{Math.round(progress)}%</span>
    </div>
  );
}

function StageList({ stage }: { stage: string }) {
  const active = stageIndex(stage);
  return (
    <ol className="boot-stages" aria-live="polite">
      {LOADING_STAGES.map((s, i) => {
        const state = i < active ? 'done' : i === active ? 'active' : 'pending';
        return (
          <li key={s.id} className={`boot-stages__item is-${state}`}>
            <span className="boot-stages__dot" aria-hidden>
              {state === 'done' ? '✓' : ''}
            </span>
            <span className="boot-stages__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function BootOverlay({ loading }: { loading: LoadingState }) {
  const [tip, setTip] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTip(t => (t + 1) % BOOT_TIPS.length), 2800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="studio-load studio-load--boot" role="status" aria-busy="true">
      <div className="studio-load__orbs" aria-hidden>
        <span className="studio-load__orb studio-load__orb--a" />
        <span className="studio-load__orb studio-load__orb--b" />
        <span className="studio-load__orb studio-load__orb--c" />
      </div>

      <div className="studio-load__brand">
        <a
          className="studio-load__mark"
          href="https://urbanqode.com"
          target="_blank"
          rel="noreferrer"
          aria-label="UrbanQode.com"
        >
          <img src="/assets/UrbanQode_Logo.png" alt="UrbanQode" width={56} height={56} />
        </a>
        <div>
          <p className="studio-load__eyebrow">
            Kit Studio · <strong>UrbanQode.com</strong>
          </p>
          <h1 className="studio-load__title">{loading.title}</h1>
          <p className="studio-load__detail">{loading.detail}</p>
        </div>
      </div>

      <div className="studio-load__panel">
        <div className="studio-load__panel-top">
          <ProgressRing progress={loading.progress} />
          <StageList stage={loading.stage} />
        </div>

        <div className="studio-load__bar" aria-hidden>
          <div className="studio-load__bar-fill" style={{ width: `${loading.progress}%` }} />
        </div>

        <p className="studio-load__tip" key={tip}>
          <span className="studio-load__tip-label">Tip</span>
          {BOOT_TIPS[tip]}
        </p>
      </div>
    </div>
  );
}

function DesignOverlay({ loading }: { loading: LoadingState }) {
  const product = useConfiguratorStore(s => s.product);
  return (
    <div className="studio-load studio-load--swap" role="status" aria-busy="true">
      <div className="studio-load__card">
        <div className="studio-load__card-visual">
          {loading.thumb ? (
            <img src={loading.thumb} alt="" className="studio-load__thumb" />
          ) : (
            <div className="studio-load__thumb studio-load__thumb--placeholder" />
          )}
          <div className="studio-load__scan" aria-hidden />
          <ProgressRing progress={loading.progress} size={72} />
        </div>
        <div className="studio-load__card-body">
          <p className="studio-load__eyebrow">{loading.mode === 'product' ? 'Garment' : 'Design'}</p>
          <h2 className="studio-load__card-title">{loading.title}</h2>
          <p className="studio-load__card-detail">
            {loading.detail}
            {loading.mode === 'design' ? ` · ${product.name}` : ''}
          </p>
          <div className="studio-load__bar studio-load__bar--compact" aria-hidden>
            <div className="studio-load__bar-fill" style={{ width: `${loading.progress}%` }} />
          </div>
          <StageList stage={loading.stage} />
        </div>
      </div>
    </div>
  );
}

export function StudioLoader() {
  const loading = useConfiguratorStore(s => s.loading);
  const error = useConfiguratorStore(s => s.error);
  const leadUnlocked = useConfiguratorStore(s => s.leadUnlocked);

  const [display, setDisplay] = useState(0);
  const target = loading?.progress ?? 0;
  /** last non-null loading snapshot for 1s hold after clear */
  const [last, setLast] = useState<LoadingState | null>(null);
  const [holdDone, setHoldDone] = useState(false);
  const phaseAtRef = useRef(0);
  const phaseKeyRef = useRef('');

  useEffect(() => {
    if (error || !leadUnlocked) return;

    if (loading) {
      const key = `${loading.mode}|${loading.stage}|${loading.title}`;
      if (phaseKeyRef.current !== key) {
        phaseKeyRef.current = key;
        phaseAtRef.current = performance.now();
        setHoldDone(false);
      }
      setLast(loading);
      return;
    }

    // loading cleared — ensure minimum 1s visibility from phase start (or from clear)
    if (!last) {
      setHoldDone(true);
      return;
    }
    const at = phaseAtRef.current || performance.now();
    const wait = Math.max(0, LOADING_MIN_MS - (performance.now() - at));
    const t = window.setTimeout(() => {
      setHoldDone(true);
      setLast(null);
      phaseKeyRef.current = '';
      phaseAtRef.current = 0;
    }, wait);
    return () => window.clearTimeout(t);
  }, [loading, error, leadUnlocked, last]);

  useEffect(() => {
    if (!loading) {
      setDisplay(0);
      return;
    }
    let raf = 0;
    const tick = () => {
      setDisplay(prev => {
        const delta = target - prev;
        if (Math.abs(delta) < 0.35) return target;
        return prev + delta * 0.2;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loading, target]);

  const smooth = useMemo(() => {
    const src = loading ?? (holdDone ? null : last);
    if (!src) return null;
    const progress = display < 0.5 ? src.progress : Math.max(src.progress, display);
    return { ...src, progress: Math.min(100, progress) };
  }, [loading, last, holdDone, display]);

  if (error || !leadUnlocked || !smooth) return null;
  if (smooth.mode === 'boot') return <BootOverlay loading={smooth} />;
  return <DesignOverlay loading={smooth} />;
}
